import React, { useEffect, useState } from "react";
import { PasswordInput, Loader } from "@mantine/core";
import type { FieldComponentProps } from "@/pages/ClientProfile/types";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";
import { useParams } from "react-router-dom";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { showFieldError } from "@utils/reviewFieldUtils.ts";

interface PasswordFieldProps extends FieldComponentProps {
  mutationType?: "profile" | "ocr";
  formId?: string;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  commonProps,
  field,
  mutationType: _mutationType = "profile",
  formId: _formId,
}) => {
  const { userId } = useParams();
  const updateProfileField = useProfileStore(
    (state) => state.updateProfileField
  );

  const [loadingState, setLoadingState] = useState(false);
  const [optimisticValue, setOptimisticValue] = useState(field.value ?? "");

  useEffect(() => {
    setOptimisticValue(field.value ?? "");
  }, [field.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value;
    setOptimisticValue(newValue);
    field.onChange(newValue);
  };

  const handleBlur = async (): Promise<void> => {
    field.onBlur();
    if (!optimisticValue) return;

    setLoadingState(true);

    try {
      await updateProfileField(userId ?? "", field.name, optimisticValue);
      // Success notification is shown by the store method
      setLoadingState(false);
    } catch (error: any) {
      showFieldError(error, field.name);
      setOptimisticValue(field.value ?? "");
      setLoadingState(false);
    }
  };

  return (
    <PasswordInput
      {...commonProps}
      {...field}
      value={optimisticValue}
      onChange={handleChange}
      onBlur={handleBlur}
      variant="filled"
      disabled={loadingState}
      rightSection={
        loadingState || commonProps.rightSection ? <Loader size="xs" /> : null
      }
      visibilityToggleIcon={({ reveal }) => (
        <span style={{ cursor: "pointer" }}>
          {reveal ? <IconEyeOff size={18} /> : <IconEye size={18} />}
        </span>
      )}
    />
  );
};

export default PasswordField;
